import { useEffect, useMemo, useState } from "react";
import { useAccount, useSwitchChain, usePublicClient, useWriteContract, useSendTransaction, useSignTypedData } from "wagmi";
import { executeApiSwap, apiRouteLabel, ZERO } from "../lib/tradingApi";
import { confirmTrade } from "../lib/competition";
import { formatUnits, formatEther, parseUnits, maxUint256, encodeFunctionData } from "viem";
import { api } from "../lib/api";
import { ArrowDownUp, ChevronDown, Settings2, RefreshCw, Info, Lock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useApp } from "../context/AppContext";
import { useTokenBalance, useAllowance, useQuote } from "../hooks/useSwap";
import { baseTokens, buildSwap, routeLabel, NATIVE } from "../lib/swap";
import { erc20Abi } from "../lib/abis";
import { fmtNum } from "../lib/format";
import { TokenSelect, useTokenList, TokenLogo } from "./TokenSelect";
import { TxStatus, runTx } from "./TxStatus";
import { SwapReview } from "./SwapReview";
import { Unavailable } from "./State";

const TokenField = ({ label, token, amount, onAmount, onPick, readOnly, side, balance, children }) => (
  <div className="rounded-xl bg-black/40 border border-white/5 p-4 focus-within:border-lime/40 transition-colors">
    <div className="flex justify-between text-[11px] font-mono text-slate-500 mb-2">
      <span>{label}</span>
      <span data-testid={`swap-${side}-balance`}>Balance: {balance != null ? fmtNum(balance, 6) : "—"}</span>
    </div>
    <div className="flex items-center gap-3">
      <input data-testid={`swap-input-${side}-amount`} inputMode="decimal" placeholder="0.0" value={amount} readOnly={readOnly}
        onChange={(e) => /^[0-9]*[.]?[0-9]*$/.test(e.target.value) && onAmount(e.target.value)}
        className="flex-1 min-w-0 bg-transparent text-2xl sm:text-3xl font-mono font-semibold text-white outline-none placeholder:text-slate-700" />
      <button data-testid={`swap-${side}-token-select`} onClick={onPick} className="flex items-center gap-2 rounded-full bg-[#1A202C] border border-white/10 hover:border-lime/50 px-3 py-1.5 text-sm font-bold">
        {token && <TokenLogo t={token} size={20} />}{token ? token.symbol : "Select"} <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
    </div>
    {children}
  </div>
);

const Row = ({ k, v, testid, tone }) => (
  <div className="flex justify-between gap-3 text-xs py-1"><span className="text-slate-500">{k}</span><span data-testid={testid} className={`font-mono text-right ${tone || "text-slate-200"}`}>{v}</span></div>
);

export const SwapCard = ({ defaultOut, sourceToken }) => {
  const { config: cfg } = useApp();
  const base = useMemo(() => baseTokens(cfg), [cfg]);
  const tokens = useTokenList(base);
  const [tokenInSel, setTokenIn] = useState(base[0]);
  const [tokenOutSel, setTokenOut] = useState(defaultOut || base.find((t) => t.hive) || base.find((t) => t.symbol === "USDG"));
  const withLogo = (t) => t && (tokens.find((x) => x.address === t.address) || t);
  const tokenIn = withLogo(tokenInSel);
  const tokenOut = withLogo(tokenOutSel);
  const symbolOf = (a) => tokens.find((t) => t.address.toLowerCase() === a.toLowerCase() && t.address !== NATIVE)?.symbol || a.slice(0, 6);
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState(0.5);
  const [picker, setPicker] = useState(null);
  const [review, setReview] = useState(false);
  const [tx, setTx] = useState(null);
  const { address, isConnected } = useAccount();
  const { chainId } = useAccount();
  const { switchChain } = useSwitchChain();
  const pc = usePublicClient({ chainId: 4663 });
  const { writeContractAsync } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();
  const { signTypedDataAsync } = useSignTypedData();
  const balIn = useTokenBalance(tokenIn);
  const balOut = useTokenBalance(tokenOut);
  const { allowance, refetch: refetchAllowance } = useAllowance(tokenIn, cfg.contracts.SwapRouter02);
  const { quote, loading, error, refresh } = useQuote({ cfg, tokenIn, tokenOut, amount, slippage, wallet: address });
  const isApi = quote?.source === "api";
  const routeText = quote ? (isApi ? apiRouteLabel(quote) : routeLabel(quote, symbolOf)) : "";

  const amtRaw = (() => { try { return amount ? parseUnits(amount, tokenIn.decimals) : 0n; } catch { return 0n; } })();
  const outFmt = quote ? formatUnits(quote.amountOut, tokenOut.decimals) : "";
  const minOut = quote ? (isApi ? quote.minOut : (quote.amountOut * BigInt(10000 - Math.round(slippage * 100))) / 10000n) : null;
  const insufficient = balIn.raw !== undefined && amtRaw > balIn.raw;
  const allowanceUnknown = !isApi && tokenIn.address !== NATIVE && (allowance === undefined || allowance === null);
  const needsApproval = !isApi && tokenIn.address !== NATIVE && !allowanceUnknown && amtRaw > allowance;

  const [gasReserve, setGasReserve] = useState(20000000000000n);
  useEffect(() => { pc?.getGasPrice().then((g) => setGasReserve(g * 800000n > 2000000000000n ? g * 800000n : 2000000000000n)).catch(() => {}); }, [pc]);
  const lowGas = tokenIn.address === NATIVE && balIn.raw !== undefined && amtRaw > 0n && amtRaw <= balIn.raw && balIn.raw - amtRaw < gasReserve;
  const setPct = (p) => {
    if (balIn.raw === undefined) return;
    let raw = (balIn.raw * BigInt(p)) / 100n;
    if (tokenIn.address === NATIVE && balIn.raw - raw < gasReserve) raw = balIn.raw > gasReserve ? balIn.raw - gasReserve : 0n;
    setAmount(formatUnits(raw, tokenIn.decimals));
  };
  const flip = () => {
    setTokenIn(tokenOut); setTokenOut(tokenIn);
    setAmount(quote ? formatUnits(quote.amountOut, tokenOut.decimals).replace(/(\.\d{0,8})\d*$/, "$1").replace(/\.$/, "") : "");
  };
  const pick = (t) => {
    if (picker === "in") { if (t.address === tokenOut?.address) setTokenOut(tokenIn); setTokenIn(t); }
    else { if (t.address === tokenIn.address) setTokenIn(tokenOut); setTokenOut(t); }
  };

  const approve = async (exact) => {
    const ok = await runTx({ pc, setTx, label: `Approve ${tokenIn.symbol}`, send: () => writeContractAsync({ chainId: 4663, address: tokenIn.address, abi: erc20Abi,
      functionName: "approve", args: [cfg.contracts.SwapRouter02, exact ? amtRaw : maxUint256] }) });
    if (ok) refetchAllowance();
  };

  const execute = async () => {
    if (isApi) {
      setReview(false);
      const ok = await executeApiSwap({ tokenIn, tokenOut, amountIn: amount, slippage, account: address, pc, setTx, sendTransactionAsync, signTypedDataAsync, runTx, sourceToken });
      if (ok) { balIn.refetch?.(); balOut.refetch?.(); setAmount(""); }
      return;
    }
    const { request } = buildSwap({ cfg, tokenIn, tokenOut, quote, slippagePct: slippage, account: address });
    setReview(false);
    if (sourceToken) {
      const data = encodeFunctionData({ abi: request.abi, functionName: request.functionName, args: request.args });
      await api.post("/swap/log", { wallet: address, to: request.address, data, token_in: tokenIn.address === NATIVE ? ZERO : tokenIn.address, token_out: tokenOut.address === NATIVE ? ZERO : tokenOut.address, source_token: sourceToken }).catch(() => {});
    }
    const ok = await runTx({ pc, setTx, label: "Swap", send: () => writeContractAsync({ ...request, chainId: 4663 }) });
    if (ok && sourceToken) confirmTrade(ok.transactionHash);
    if (ok) { balIn.refetch?.(); balOut.refetch?.(); setAmount(""); }
  };

  const cta = (() => {
    if (!isConnected) return { label: "Connect a wallet to swap", disabled: true };
    if (chainId !== 4663) return { label: "Switch to Robinhood Chain", onClick: () => switchChain({ chainId: 4663 }) };
    if (!amtRaw) return { label: "Enter an amount", disabled: true };
    if (insufficient) return { label: `Insufficient ${tokenIn.symbol} balance`, disabled: true };
    if (lowGas) return { label: "Leave a little ETH for network gas", disabled: true };
    if (!quote) return { label: loading ? "Fetching on-chain quote…" : "No route available", disabled: true };
    if (!cfg.swap_execution_enabled) return { label: "Execution locked — awaiting operator approval", disabled: true, locked: true };
    if (allowanceUnknown) return { label: "Checking token allowance…", disabled: true };
    if (needsApproval) return { label: `Approve ${tokenIn.symbol}`, onClick: () => approve(true) };
    return { label: "Review swap", onClick: () => setReview(true) };
  })();

  return (
    <section data-testid="swap-card" className="glass rounded-xl p-5 circuit-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-sm font-bold tracking-[0.2em] uppercase">Swap</h2>
        <div className="flex items-center gap-2">
          <button data-testid="swap-refresh-quote" onClick={refresh} className="p-1.5 text-slate-500 hover:text-lime"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
          <Popover>
            <PopoverTrigger asChild><button data-testid="swap-settings-button" className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs text-slate-300 hover:border-lime/40"><Settings2 className="h-3.5 w-3.5" /> {slippage}%</button></PopoverTrigger>
            <PopoverContent className="bg-[#12161F] border-lime/20 text-slate-200 w-64">
              <p className="text-xs font-semibold mb-2">Slippage tolerance</p>
              <div className="flex gap-1.5">
                {[0.1, 0.5, 1].map((s) => <button key={s} data-testid={`slippage-${s}`} onClick={() => setSlippage(s)} className={`flex-1 rounded py-1 text-xs font-mono ${slippage === s ? "bg-lime text-black" : "bg-white/5"}`}>{s}%</button>)}
                <input data-testid="slippage-custom-input" type="number" min="0.01" max="20" step="0.1" value={slippage} onChange={(e) => setSlippage(Math.min(20, Math.max(0.01, Number(e.target.value) || 0.5)))} className="w-16 rounded bg-black/40 border border-white/10 px-1 text-xs font-mono" />
              </div>
              {slippage > 3 && <p className="text-[11px] text-warning mt-2">High slippage increases the risk of an unfavourable fill (front-running).</p>}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <TokenField label="You pay" side="pay" token={tokenIn} amount={amount} onAmount={setAmount} onPick={() => setPicker("in")} balance={balIn.formatted}>
        <div className="flex gap-1.5 mt-3">
          {[25, 50, 75, 100].map((p) => <button key={p} data-testid={`swap-pct-${p}`} disabled={balIn.raw === undefined} onClick={() => setPct(p)} className="rounded-md bg-white/5 hover:bg-lime/15 hover:text-lime disabled:opacity-40 px-2.5 py-1 text-[11px] font-mono font-semibold">{p === 100 ? "MAX" : `${p}%`}</button>)}
        </div>
      </TokenField>
      <div className="flex justify-center -my-3 relative z-10">
        <button data-testid="swap-direction-button" onClick={flip} className="hex h-10 w-10 grid place-items-center bg-[#1A202C] border border-lime/30 text-lime hover:rotate-180 transition-transform duration-300"><ArrowDownUp className="h-4 w-4" /></button>
      </div>
      <TokenField label="You receive (estimated)" side="receive" token={tokenOut} amount={outFmt ? fmtNum(outFmt, 8).replace(/,/g, "") : ""} readOnly onPick={() => setPicker("out")} balance={balOut.formatted} />

      <div className="mt-4 rounded-lg bg-black/25 border border-white/5 px-3 py-2" data-testid="swap-quote-details">
        {error ? <p data-testid="swap-quote-error" className="text-xs text-danger py-1">{error}</p> : quote ? (<>
          <Row k="Rate" v={`1 ${tokenIn.symbol} = ${fmtNum(Number(outFmt) / Number(amount), 8)} ${tokenOut.symbol}`} testid="swap-rate" />
          <Row k={`Minimum received (${slippage}% slippage)`} v={`${fmtNum(formatUnits(minOut, tokenOut.decimals), 8)} ${tokenOut.symbol}`} testid="swap-min-received" />
          {quote.feeTerms?.discounts?.length > 0 && <Row k="Fee discount" testid="swap-fee-discount" tone="text-lime"
            v={`${quote.feeTerms.discounts.map((x) => x.label).join(" + ")} · ${quote.feeTerms.base_bips / 100}% → ${quote.feeTerms.bips / 100}%`} />}
          {quote.fees?.length > 0 && <Row k={`HIVEVORA swap fee (${quote.fees.reduce((a, f) => a + f.bips, 0) / 100}%)`} testid="swap-hivevora-fee"
            v={quote.fees.map((f) => `${fmtNum(formatUnits(BigInt(f.amount), tokenOut.decimals), 6)} ${tokenOut.symbol} ${f.roles.join("+")}`).join(" · ")} />}
          <Row k="Price impact" v={`${quote.priceImpact < 0.01 ? "<0.01" : quote.priceImpact.toFixed(2)}%`} testid="swap-price-impact" tone={quote.priceImpact > 5 ? "text-danger" : quote.priceImpact > 1 ? "text-warning" : "text-lime"} />
          <Row k="Est. network gas" v={`${fmtNum(formatEther(quote.gasWei), 8)} ETH`} testid="swap-gas-estimate" />
          <Row k="Route" v={routeText} testid="swap-route" />
          <Row k="Router" v={isApi ? "Uniswap Trading API (v2/v3/v4)" : "HIVEVORA on-chain v3 router"} testid="swap-router-source" />
          {quote.hasHooks && <p data-testid="swap-hooks-warning" className="text-[10px] text-warning py-1">Route uses a Uniswap v4 pool with a custom hook contract. Hooks can add custom logic; review the route on the explorer if unsure.</p>}
          {quote.apiError && <p data-testid="swap-api-fallback" className="text-[10px] text-slate-500 py-1">Trading API: {quote.apiError} — showing HIVEVORA's direct Uniswap v3 route instead.</p>}
          {!quote.fees?.length && <Row k="HIVEVORA swap fee" v="none on this pair" testid="swap-platform-fee" />}
        </>) : <p className="text-xs text-slate-500 py-1 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Live quotes from the Uniswap Trading API (v2/v3/v4) with on-chain v3 fallback.</p>}
      </div>

      {needsApproval && cfg.swap_execution_enabled && quote && (
        <button data-testid="swap-approve-unlimited" onClick={() => approve(false)} className="mt-2 text-[11px] text-slate-400 hover:text-lime underline">Approve unlimited instead (not recommended)</button>
      )}
      <button data-testid="swap-execute-button" disabled={cta.disabled} onClick={cta.onClick} className="btn-lime w-full mt-4 py-3.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
        {cta.locked && <Lock className="h-4 w-4" />} {cta.label}
      </button>
      {cfg.swap_execution_enabled && (
        <p data-testid="swap-live-notice" className="mt-3 text-[10px] text-slate-500 leading-relaxed">Live on Robinhood Chain mainnet. Swaps execute on Uniswap protocol contracts (Universal Router / SwapRouter02) from your wallet; HIVEVORA never holds funds. Swaps of HIVEVORA-listed or launched tokens carry a disclosed swap fee (shown above), paid on-chain in the same transaction. Only proceed with amounts you can afford to lose.</p>
      )}
      {!cfg.swap_execution_enabled && (
        <div className="mt-3"><Unavailable testid="swap-execution-locked" title="Swap execution disabled by operator"
          reason="Quotes, balances, approvals checks and calldata are live against Uniswap v3 on Robinhood Chain mainnet. Signing is locked until the operator sets SWAP_EXECUTION_ENABLED=true after explicit approval. No funds can move." /></div>
      )}
      <div className="mt-3"><TxStatus tx={tx} testid="swap-tx-status" /></div>

      <TokenSelect open={!!picker} onOpenChange={(o) => !o && setPicker(null)} tokens={tokens} onSelect={pick} />
      {quote && <SwapReview open={review} onOpenChange={setReview} onConfirm={execute} tokenIn={tokenIn} tokenOut={tokenOut} amount={amount} outFmt={outFmt}
        minOut={formatUnits(minOut, tokenOut.decimals)} quote={quote} slippage={slippage} router={cfg.contracts.SwapRouter02} route={routeText} />}
    </section>
  );
};
